import { useMemo } from 'react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { SVGS } from '../../assets';
import moment from 'moment';
import { CommonButton } from '../../components';
import { TouchableOpacity } from '../../common';
import { useClockInLogic } from '../../hooks';
import { ClockInSkeleton } from './clock-in-skeleton';
import { NoInternetView } from './no-internet-view';
import './clock-in-modal.scss';
import { getConfigValue } from '@zonofi/common/functions/get-config-value';

interface ClockInModalProps {
  open: boolean;
  onClose: () => void;
}
const GOOGLE_MAP_API_KEY = getConfigValue('GOOGLE_MAP_API_KEY');

export const ClockInModal: React.FC<ClockInModalProps> = ({
  open,
  onClose,
}) => {
  const {
    isOnline,
    clockInInfo,
    currentLocation,
    onClockInOutPress,
    buttonLoading,
    successModalState,
    setSuccessModalState,
  } = useClockInLogic({ open, onClose });

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });

  const timeValue = useMemo(() => {
    return successModalState.type === 'CLOCK-IN'
      ? successModalState.data?.clockIn
      : successModalState.data?.clockOut;
  }, [successModalState]);

  const infoItems = useMemo(
    () => [
      {
        key: 'location',
        title: 'Location',
        icon: SVGS.ZotokLoc,
        isBold: false,
        lines: [
          currentLocation?.formatted_address || '',
          `Lat&Lon: ${currentLocation?.lat}, ${currentLocation?.lng}`,
        ],
      },
      {
        key: 'datetime',
        title: 'Date & Time',
        icon: SVGS.CalendarSchedule,
        isBold: true,
        lines: [moment().format('dddd, DD MMM YYYY - hh : mm : ss A')],
      },
    ],
    [currentLocation]
  );

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{ className: 'clockin-modal__drawer' }}
    >
      <Box className="clockin-modal__container">
        {isOnline ? (
          successModalState.show ? (
            <div className="clockin-modal__success-view">
              <div className="clockin-modal__success-view__heading font-semibold">{`${successModalState.type === 'CLOCK-IN' ? 'Clock-in' : 'Clock-out'} Successful`}</div>
              <img src={SVGS.ClockSuccess} />

              <div className="clockin-modal__success-view__time-text font-semibold">
                {moment(timeValue).format('hh : mm : ss A')}
              </div>
              <div className="clockin-modal__success-view__date-text font-medium">
                {moment(timeValue).format('dddd, DD MMM YYYY')}
              </div>

              <CommonButton
                text={'Done'}
                onPress={() => {
                  onClose?.();
                  setSuccessModalState({
                    show: false,
                    type: undefined,
                    data: undefined,
                  });
                }}
                buttonStyle={'clockin-modal__success-btn'}
              />
            </div>
          ) : (
            <>
              <div className="clockin-modal__header font-semibold">
                {clockInInfo?.tripId ? 'Confirm Clock Out' : 'Clock In'}
              </div>

              {!isLoaded || !currentLocation ? (
                <ClockInSkeleton />
              ) : (
                <>
                  {/* MAP SECTION */}
                  <div className="clockin-modal__map">
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      zoom={15}
                      center={{
                        lat: Number(currentLocation.lat),
                        lng: Number(currentLocation.lng),
                      }}
                      options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        zoomControl: false,
                        disableDefaultUI: true,
                      }}
                    >
                      <MarkerF
                        position={{
                          lat: Number(currentLocation.lat),
                          lng: Number(currentLocation.lng),
                        }}
                        icon={{
                          url: SVGS.ZotokLoc,
                          scaledSize: new window.google.maps.Size(40, 40),
                        }}
                      />
                    </GoogleMap>
                  </div>

                  {/* INFO SECTIONS */}
                  {infoItems.map((item) => (
                    <div key={item.key} className="clockin-modal__info">
                      <div className="clockin-modal__info-title font-semibold">
                        {item.title}
                      </div>

                      <div className="clockin-modal__info-row">
                        <img src={item.icon} />

                        <div className="clockin-modal__info-lines">
                          {item.lines.map((line, index) => (
                            <span
                              key={index}
                              className={`clockin-modal__info-text ${
                                item.isBold ? 'font-semibold' : 'font-regular'
                              }`}
                            >
                              {line}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ACTIONS */}
              <CommonButton
                text={`Yes, ${clockInInfo?.tripId ? 'Clock Out' : 'Clock In'}`}
                onPress={onClockInOutPress}
                buttonStyle={'clockin-modal__confirm-btn'}
                loading={buttonLoading}
                disabled={!isLoaded || !currentLocation}
                spinnerColor={'white'}
                spinnerStyle="clockin-modal__btn-spinner"
              />

              <TouchableOpacity
                onPress={onClose}
                className="clockin-modal__cancel-btn"
              >
                <div className="clockin-modal__cancel-text font-semibold">
                  {'Cancel'}
                </div>
              </TouchableOpacity>
            </>
          )
        ) : (
          <NoInternetView />
        )}
      </Box>
    </SwipeableDrawer>
  );
};
